import { useEffect } from "react";
import { arrayUnion, doc, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../context/AuthProvider";
import { useLocationStore } from "../store/location.store";

function inAndroidApp(): boolean {
  return typeof navigator !== "undefined" && /RuchiraApp/.test(navigator.userAgent || "");
}

/**
 * Runs only inside the Capacitor app. Saves the FCM token onto the signed-in
 * user and lets the hardware back button dismiss the location sheet.
 * Included on the next `npm run build`; the website never matches the UA.
 */
export function AndroidShell() {
  const { user } = useAuth();

  useEffect(() => {
    if (!inAndroidApp()) return;
    const onBack = (event: Event) => {
      if (useLocationStore.getState().isPromptOpen) {
        event.preventDefault();
        useLocationStore.getState().setPromptOpen(false);
      }
    };
    window.addEventListener("ruchira-android-back", onBack);
    return () => window.removeEventListener("ruchira-android-back", onBack);
  }, []);

  useEffect(() => {
    if (!user?.uid || !inAndroidApp()) return;
    const save = (token: string) => {
      updateDoc(doc(db, "users", user.uid), { fcmTokens: arrayUnion(token) }).catch((error) => {
        console.warn("Could not save FCM token", error);
      });
    };
    try {
      const existing = localStorage.getItem("ruchira_fcm_token");
      if (existing) save(existing);
    } catch {
      /* ignore */
    }
    const onToken = (event: Event) => {
      const token = (event as CustomEvent<{ token?: string }>).detail?.token;
      if (token) save(token);
    };
    window.addEventListener("ruchira-push-token", onToken);
    return () => window.removeEventListener("ruchira-push-token", onToken);
  }, [user]);

  return null;
}
